import type { CasOrCategory } from '../types/db.ts';
import type { EpaNumericFlag } from '../../cross-app-shared/types/epa-api.ts';
import type {
  TRIChemicalActivitiesAndUsesDataType,
  TRIMaximumChemicalAmountCodeType,
  TRIOnsiteRecoveryProcessDataType,
  TRIOnsiteRecyclingProcessDataType,
  TRIOnsiteReleaseQuantityDataType,
  TRIPOTWWasteQuantityDataType,
  TRISourceReductionQuantityDataType,
  TRITotalYearlyQuantityDataType,
  TRITransferLocationDataType,
} from '../types/form-r.ts';
import { formRBasisFromTransfers, type ReleasesByChemicalAndFormRLine } from './form-r/form-r-utils.ts';
import type { FormRLeafLine } from './form-r/form-r-sections.ts';

/**
 * See: common/data/epa-api-form-r-ez-docs.html
 * See also: https://enviro.epa.gov/envirofacts/metadata/table/tri/tri_form_r_ez
 * I have removed fields that I don't think are worth comparing.
 */
export interface EpaApiTriFormREZForComparison {
  // Chemical and document info
  reporting_year: string;
  cas_registry_number: CasOrCategory;

  // Section 3
  manufacture: EpaNumericFlag;
  process: EpaNumericFlag;
  otherwise_use: EpaNumericFlag;

  // Section 3 - Manufacture
  produce: EpaNumericFlag;
  imported: EpaNumericFlag;
  used_processed: EpaNumericFlag;
  sale_distribution: EpaNumericFlag;
  byproduct: EpaNumericFlag;
  manufacture_impurity: EpaNumericFlag;

  // Section 3 - Process
  reactant: EpaNumericFlag;
  reactant_feedstocks: EpaNumericFlag;
  reactant_initiators: EpaNumericFlag;
  reactant_intermediates: EpaNumericFlag;
  reactant_other: EpaNumericFlag;
  reactant_raw_materials: EpaNumericFlag;

  formulation_component: EpaNumericFlag;
  formul_comp_additives: EpaNumericFlag;
  formul_comp_dyes: EpaNumericFlag;
  formul_comp_emulsifiers: EpaNumericFlag;
  formul_comp_flame_retardants: EpaNumericFlag;
  formul_comp_inhibitors: EpaNumericFlag;
  formul_comp_initiators: EpaNumericFlag;
  formul_comp_lubricants: EpaNumericFlag;
  formul_comp_other: EpaNumericFlag;
  formul_comp_reac_diluents: EpaNumericFlag;
  formul_comp_rheological_mods: EpaNumericFlag;
  formul_comp_solvents: EpaNumericFlag;
  formul_comp_surfactants: EpaNumericFlag;

  article_component: EpaNumericFlag;
  repackaging: EpaNumericFlag;
  process_impurity: EpaNumericFlag;
  processed_recycling: EpaNumericFlag;

  // Section 3 - Otherwise Use
  ancillary: EpaNumericFlag;
  ancl_other_use_cleaner: EpaNumericFlag;
  ancl_other_use_construct_matls: EpaNumericFlag;
  ancl_other_use_degreaser: EpaNumericFlag;
  ancl_other_use_flame_retardant: EpaNumericFlag;
  ancl_other_use_fuel: EpaNumericFlag;
  ancl_other_use_lubricant: EpaNumericFlag;
  ancl_other_use_other: EpaNumericFlag;
  ancl_other_use_waste_treat: EpaNumericFlag;
  ancl_other_use_water_treat: EpaNumericFlag;

  chem_processing_aid: EpaNumericFlag;
  chem_process_aid_catalysts: EpaNumericFlag;
  chem_process_aid_inhibitors: EpaNumericFlag;
  chem_process_aid_initiators: EpaNumericFlag;
  chem_process_aid_other: EpaNumericFlag;
  chem_process_aid_process_solvs: EpaNumericFlag;
  chem_process_aid_react_termin: EpaNumericFlag;
  chem_process_aid_solution_buf: EpaNumericFlag;

  manufacture_aid: EpaNumericFlag;
  manuf_aid_coolants: EpaNumericFlag;
  manuf_aid_hydraulic_fluids: EpaNumericFlag;
  manuf_aid_metalworking_fluids: EpaNumericFlag;
  manuf_aid_other: EpaNumericFlag;
  manuf_aid_process_lubricants: EpaNumericFlag;
  manuf_aid_refrigerants: EpaNumericFlag;

  // Section 4
  max_amount_of_chem: string | null;

  // Section 5.1
  fugitive_basis_est_code: string | null;
  fugitive_release_na: string | null;
  fugitive_tot_rel: number | null;

  // Section 5.2
  stack_basis_est_code: string | null;
  stack_release_na: string | null;
  stack_tot_rel: number | null;

  // Section 5.3
  water_basis_est_code_1: string | null;
  water_basis_est_code_2: string | null;
  water_basis_est_code_3: string | null;
  water_body_name_1: string | null;
  water_body_name_2: string | null;
  water_body_name_3: string | null;
  water_release_1: number | null;
  water_release_2: number | null;
  water_release_3: number | null;
  water_release_na_1: string | null;
  water_release_na_2: string | null;
  water_release_na_3: string | null;
  water_storm_water_na_1: string | null;
  water_storm_water_na_2: string | null;
  water_storm_water_na_3: string | null;
  storm_water_percent_1: number | null;
  storm_water_percent_2: number | null;
  storm_water_percent_3: number | null;
  reach_code_1: string | null;
  reach_code_2: string | null;
  reach_code_3: string | null;

  // Section 5.4.1
  uninj_i_basis_est_code: string | null;
  uninj_i_release_na: string | null;
  uninj_i_tot_rel: number | null;

  // Section 5.4.2
  uninj_iiv_basis_est_code: string | null;
  uninj_iiv_release_na: string | null;
  uninj_iiv_tot_rel: number | null;

  // Section 5.5.1A
  rcra_c_basis_est_code: string | null;
  rcra_c_release_na: string | null;
  rcra_c_tot_rel: number | null;

  // Section 5.5.1B
  oth_landf_basis_est_code: string | null;
  oth_landf_release_na: string | null;
  oth_landf_tot_rel: number | null;

  // Section 5.5.2
  land_trea_basis_est_code: string | null;
  land_trea_release_na: string | null;
  land_trea_tot_rel: number | null;

  // Section 5.5.3A
  sub_surf_imp_basis_est_code: string | null;
  sub_surf_imp_release_na: string | null;
  sub_surf_imp_tot_rel: number | null;

  // Section 5.5.3B
  oth_surf_imp_basis_est_code: string | null;
  oth_surf_imp_release_na: string | null;
  oth_surf_imp_tot_rel: number | null;

  // Section 5.5.4
  oth_disp_basis_est_code: string | null;
  oth_disp_release_na: string | null;
  oth_disp_tot_rel: number | null;

  // Section 6.1
  potw_name_1: string | null;
  potw_name_2: string | null;
  potw_1_total_transfer: number | null;
  potw_2_total_transfer: number | null;
  potw_basis_est_code_1: string | null;
  potw_basis_est_code_2: string | null;
  potw_p30_disch_waster: number | null;
  potw_p31_disch_other_act: number | null;
  potw_p32_released_air: number | null;
  potw_p33_sludge_disposal: number | null;
  potw_p34_sludge_inc_metals: number | null;
  potw_p35_sludge_agric: number | null;
  potw_p36_other_disposal: number | null;
  potw_p37_other_treatment: number | null;
  potw_p38_sludge_incin: number | null;
  potw_p39_exper_treatment: number | null;
  potw_tot_transfer: number | null;

  // Section 6.2
  off_site_loc_name_1: string | null;
  off_site_loc_name_2: string | null;
  off_site_loc_name_3: string | null;
  off_site_loc_name_4: string | null;
  off_site_loc_name_5: string | null;
  disp_land_treatment: number | null; // M73 Land Treatment
  disp_landfill_surf_imp: number | null; // M64 Other Landfills M65 RCRA Subtitle C Landfills M66 Subtitle C Surface Impoundments M67 Other Surface Impoundments
  disp_other_land_disp: number | null; // M79 Other Land Disposal
  disp_other_offsite_mgmt: number | null; // M90 Other Off-Site Management
  disp_solid_stab: number | null; // M41 Solidification/Stabilization
  disp_storage_only: number | null; // M10 Storage Only
  disp_trans_waste_broker: number | null; // M94 Transfer to Waste Broker - Disposal
  disp_ungrnd_inj: number | null; // M81 Underground Injection to Class I Wells M82 Underground Injection to Class II-V Wells
  disp_wastewater_treat: number | null; // M62 Wastewater Treatment (Excluding POTW)
  disp_unknown: number | null; // M99 Management Method Unknown

  // Section 7A - needs to be pulled separately.

  // Section 7B
  onsite_energy_proc_codes: string | null;

  // Section 7C
  onsite_recycle_proc_codes: string | null;

  // Section 8.1
  rel_81a_curr_yr_na: string | null;
  rel_81a_curr_yr_qty: number | null;
  rel_81b_curr_yr_na: string | null;
  rel_81b_curr_yr_qty: number | null;
  rel_81c_curr_yr_na: string | null;
  rel_81c_curr_yr_qty: number | null;
  rel_81d_curr_yr_na: string | null;
  rel_81d_curr_yr_qty: number | null;

  // Section 8.2
  energy_onsite_curr_yr_na: string | null;
  energy_onsite_curr_yr_qty: number | null;

  // Section 8.3
  energy_offsite_curr_yr_na: string | null;
  energy_offsite_curr_yr_qty: number | null;

  // Section 8.4
  recyc_onsite_curr_yr_na: string | null;
  recyc_onsite_curr_yr_qty: number | null;

  // Section 8.5
  recyc_offsite_curr_yr_na: string | null;
  recyc_offsite_curr_yr_qty: number | null;

  // Section 8.6
  treated_onsite_curr_yr_na: string | null;
  treated_onsite_curr_yr_qty: number | null;

  // Section 8.7
  treated_offsite_curr_yr_na: string | null;
  treated_offsite_curr_yr_qty: number | null;

  // Section 8.8
  one_time_release_qty: number | null;
  one_time_release_qty_na: string | null;

  // Section 8.9
  prod_ratio_or_activity: string | null;
  production_ratio: number | null;
  production_ratio_na: string | null;
}

export const epaEzFieldNameMappings: Record<keyof EpaApiTriFormREZForComparison, string> = {
  // Basic info
  reporting_year: 'Reporting Year',
  cas_registry_number: 'CAS Registry Number',

  // Section 3 - Activities and Uses
  manufacture: '3 - Manufacture',
  process: '3 - Process',
  otherwise_use: '3 - Otherwise Use',

  // Section 3 - Manufacture subcategories
  produce: '3 - Produce',
  imported: '3 - Imported',
  used_processed: '3 - Used/Processed',
  sale_distribution: '3 - Sale/Distribution',
  byproduct: '3 - Byproduct',
  manufacture_impurity: '3 - Manufacture Impurity',

  // Section 3 - Process subcategories
  reactant: '3 - Reactant',
  reactant_feedstocks: '3 - Reactant - Feedstocks',
  reactant_initiators: '3 - Reactant - Initiators',
  reactant_intermediates: '3 - Reactant - Intermediates',
  reactant_other: '3 - Reactant - Other',
  reactant_raw_materials: '3 - Reactant - Raw Materials',

  formulation_component: '3 - Formulation Component',
  formul_comp_additives: '3 - Formulation - Additives',
  formul_comp_dyes: '3 - Formulation - Dyes',
  formul_comp_emulsifiers: '3 - Formulation - Emulsifiers',
  formul_comp_flame_retardants: '3 - Formulation - Flame Retardants',
  formul_comp_inhibitors: '3 - Formulation - Inhibitors',
  formul_comp_initiators: '3 - Formulation - Initiators',
  formul_comp_lubricants: '3 - Formulation - Lubricants',
  formul_comp_other: '3 - Formulation - Other',
  formul_comp_reac_diluents: '3 - Formulation - Reaction Diluents',
  formul_comp_rheological_mods: '3 - Formulation - Rheological Modifiers',
  formul_comp_solvents: '3 - Formulation - Solvents',
  formul_comp_surfactants: '3 - Formulation - Surfactants',

  article_component: '3 - Article Component',
  repackaging: '3 - Repackaging',
  process_impurity: '3 - Process Impurity',
  processed_recycling: '3 - Processed Recycling',

  // Section 3 - Otherwise Use subcategories
  ancillary: '3 - Ancillary/Other Use',
  ancl_other_use_cleaner: '3 - Ancillary/Other Use - Cleaner',
  ancl_other_use_construct_matls: '3 - Ancillary/Other Use - Construction Materials',
  ancl_other_use_degreaser: '3 - Ancillary/Other Use - Degreaser',
  ancl_other_use_flame_retardant: '3 - Ancillary/Other Use - Flame Retardant',
  ancl_other_use_fuel: '3 - Ancillary/Other Use - Fuel',
  ancl_other_use_lubricant: '3 - Ancillary/Other Use - Lubricant',
  ancl_other_use_other: '3 - Ancillary/Other Use - Other',
  ancl_other_use_waste_treat: '3 - Ancillary/Other Use - Waste Treatment',
  ancl_other_use_water_treat: '3 - Ancillary/Other Use - Water Treatment',

  chem_processing_aid: '3 - Chemical Processing Aid',
  chem_process_aid_catalysts: '3 - Chemical Processing Aid - Catalysts',
  chem_process_aid_inhibitors: '3 - Chemical Processing Aid - Inhibitors',
  chem_process_aid_initiators: '3 - Chemical Processing Aid - Initiators',
  chem_process_aid_other: '3 - Chemical Processing Aid - Other',
  chem_process_aid_process_solvs: '3 - Chemical Processing Aid - Process Solvents',
  chem_process_aid_react_termin: '3 - Chemical Processing Aid - Reaction Terminators',
  chem_process_aid_solution_buf: '3 - Chemical Processing Aid - Solution Buffers',

  manufacture_aid: '3 - Manufacturing Aid',
  manuf_aid_coolants: '3 - Manufacturing Aid - Coolants',
  manuf_aid_hydraulic_fluids: '3 - Manufacturing Aid - Hydraulic Fluids',
  manuf_aid_metalworking_fluids: '3 - Manufacturing Aid - Metalworking Fluids',
  manuf_aid_other: '3 - Manufacturing Aid - Other',
  manuf_aid_process_lubricants: '3 - Manufacturing Aid - Process Lubricants',
  manuf_aid_refrigerants: '3 - Manufacturing Aid - Refrigerants',

  // Section 4 - Maximum Amount
  max_amount_of_chem: '4 - Maximum Amount of Chemical',

  // Section 5.1 - Fugitive Air Emissions
  fugitive_basis_est_code: '5.1 - Fugitive Emissions - Basis of Estimate',
  fugitive_release_na: '5.1 - Fugitive Emissions - Applicability',
  fugitive_tot_rel: '5.1 - Fugitive Emissions - Total Released',

  // Section 5.2 - Stack Air Emissions
  stack_basis_est_code: '5.2 - Stack Emissions - Basis of Estimate',
  stack_release_na: '5.2 - Stack Emissions - Applicability',
  stack_tot_rel: '5.2 - Stack Emissions - Total Released',

  // Section 5.3 - Water Releases
  water_basis_est_code_1: '5.3 - Water Release 1 - Basis of Estimate',
  water_basis_est_code_2: '5.3 - Water Release 2 - Basis of Estimate',
  water_basis_est_code_3: '5.3 - Water Release 3 - Basis of Estimate',
  water_body_name_1: '5.3 - Water Body Name 1',
  water_body_name_2: '5.3 - Water Body Name 2',
  water_body_name_3: '5.3 - Water Body Name 3',
  water_release_1: '5.3 - Water Release 1 - Amount',
  water_release_2: '5.3 - Water Release 2 - Amount',
  water_release_3: '5.3 - Water Release 3 - Amount',
  water_release_na_1: '5.3 - Water Release 1 - Applicability',
  water_release_na_2: '5.3 - Water Release 2 - Applicability',
  water_release_na_3: '5.3 - Water Release 3 - Applicability',
  water_storm_water_na_1: '5.3 - Storm Water 1 - Applicability',
  water_storm_water_na_2: '5.3 - Storm Water 2 - Applicability',
  water_storm_water_na_3: '5.3 - Storm Water 3 - Applicability',
  storm_water_percent_1: '5.3 - Storm Water Percent 1',
  storm_water_percent_2: '5.3 - Storm Water Percent 2',
  storm_water_percent_3: '5.3 - Storm Water Percent 3',
  reach_code_1: '5.3 - Reach Code 1',
  reach_code_2: '5.3 - Reach Code 2',
  reach_code_3: '5.3 - Reach Code 3',

  // Section 5.4.1 - Underground Injection Class I
  uninj_i_basis_est_code: '5.4.1 - Underground Injection Class I - Basis of Estimate',
  uninj_i_release_na: '5.4.1 - Underground Injection Class I - Applicability',
  uninj_i_tot_rel: '5.4.1 - Underground Injection Class I - Total Released',

  // Section 5.4.2 - Underground Injection Class II-V
  uninj_iiv_basis_est_code: '5.4.2 - Underground Injection Class II-V - Basis of Estimate',
  uninj_iiv_release_na: '5.4.2 - Underground Injection Class II-V - Applicability',
  uninj_iiv_tot_rel: '5.4.2 - Underground Injection Class II-V - Total Released',

  // Section 5.5.1A - RCRA Subtitle C Landfills
  rcra_c_basis_est_code: '5.5.1A - RCRA Subtitle C Landfills - Basis of Estimate',
  rcra_c_release_na: '5.5.1A - RCRA Subtitle C Landfills - Applicability',
  rcra_c_tot_rel: '5.5.1A - RCRA Subtitle C Landfills - Total Released',

  // Section 5.5.1B - Other Landfills
  oth_landf_basis_est_code: '5.5.1B - Other Landfills - Basis of Estimate',
  oth_landf_release_na: '5.5.1B - Other Landfills - Applicability',
  oth_landf_tot_rel: '5.5.1B - Other Landfills - Total Released',

  // Section 5.5.2 - Land Treatment
  land_trea_basis_est_code: '5.5.2 - Land Treatment - Basis of Estimate',
  land_trea_release_na: '5.5.2 - Land Treatment - Applicability',
  land_trea_tot_rel: '5.5.2 - Land Treatment - Total Released',

  // Section 5.5.3A - Surface Impoundments
  sub_surf_imp_basis_est_code: '5.5.3A - Surface Impoundments - Basis of Estimate',
  sub_surf_imp_release_na: '5.5.3A - Surface Impoundments - Applicability',
  sub_surf_imp_tot_rel: '5.5.3A - Surface Impoundments - Total Released',

  // Section 5.5.3B - Other Surface Impoundments
  oth_surf_imp_basis_est_code: '5.5.3B - Other Surface Impoundments - Basis of Estimate',
  oth_surf_imp_release_na: '5.5.3B - Other Surface Impoundments - Applicability',
  oth_surf_imp_tot_rel: '5.5.3B - Other Surface Impoundments - Total Released',

  // Section 5.5.4 - Other Disposal
  oth_disp_basis_est_code: '5.5.4 - Other Disposal - Basis of Estimate',
  oth_disp_release_na: '5.5.4 - Other Disposal - Applicability',
  oth_disp_tot_rel: '5.5.4 - Other Disposal - Total Released',

  // Section 6.1 - POTW Transfers
  potw_name_1: '6.1 - POTW Name 1',
  potw_name_2: '6.1 - POTW Name 2',
  potw_1_total_transfer: '6.1 - POTW 1 - Total Transfer',
  potw_2_total_transfer: '6.1 - POTW 2 - Total Transfer',
  potw_basis_est_code_1: '6.1 - POTW 1 - Basis of Estimate',
  potw_basis_est_code_2: '6.1 - POTW 2 - Basis of Estimate',
  potw_p30_disch_waster: '6.1 - POTW - Discharge to Wastewater (P30)',
  potw_p31_disch_other_act: '6.1 - POTW - Discharge Other Activities (P31)',
  potw_p32_released_air: '6.1 - POTW - Released to Air (P32)',
  potw_p33_sludge_disposal: '6.1 - POTW - Sludge Disposal (P33)',
  potw_p34_sludge_inc_metals: '6.1 - POTW - Sludge Including Metals (P34)',
  potw_p35_sludge_agric: '6.1 - POTW - Sludge Agricultural (P35)',
  potw_p36_other_disposal: '6.1 - POTW - Other Disposal (P36)',
  potw_p37_other_treatment: '6.1 - POTW - Other Treatment (P37)',
  potw_p38_sludge_incin: '6.1 - POTW - Sludge Incineration (P38)',
  potw_p39_exper_treatment: '6.1 - POTW - Experimental Treatment (P39)',
  potw_tot_transfer: '6.1 - POTW - Total Transfer',

  // Section 6.2 - Off-site Locations
  off_site_loc_name_1: '6.2 - Off-site Location Name 1',
  off_site_loc_name_2: '6.2 - Off-site Location Name 2',
  off_site_loc_name_3: '6.2 - Off-site Location Name 3',
  off_site_loc_name_4: '6.2 - Off-site Location Name 4',
  off_site_loc_name_5: '6.2 - Off-site Location Name 5',
  disp_land_treatment: '6.2 - Disposal - Land Treatment (M73)',
  disp_landfill_surf_imp: '6.2 - Disposal - Landfill Surface Impoundment (M64, M65, M66, M67)',
  disp_other_land_disp: '6.2 - Disposal - Other Land Disposal (M79)',
  disp_other_offsite_mgmt: '6.2 - Disposal - Other Off-site Management (M90)',
  disp_solid_stab: '6.2 - Disposal - Solidification/Stabilization (M41)',
  disp_storage_only: '6.2 - Disposal - Storage Only (M10)',
  disp_trans_waste_broker: '6.2 - Disposal - Transfer to Waste Broker (M94)',
  disp_ungrnd_inj: '6.2 - Disposal - Underground Injection (M81, M82)',
  disp_wastewater_treat: '6.2 - Disposal - Wastewater Treatment (M62)',
  disp_unknown: '6.2 - Disposal - Unknown Method (M99)',

  // Section 7B - On-site Energy Recovery
  onsite_energy_proc_codes: '7B - On-site Energy Recovery Process Codes',

  // Section 7C - On-site Recycling
  onsite_recycle_proc_codes: '7C - On-site Recycling Process Codes',

  // Section 8.1 - Disposal Quantities
  rel_81a_curr_yr_na: '8.1a - Disposal - Applicability',
  rel_81a_curr_yr_qty: '8.1a - Disposal - Current Year Quantity',
  rel_81b_curr_yr_na: '8.1b - Disposal - Applicability',
  rel_81b_curr_yr_qty: '8.1b - Disposal - Current Year Quantity',
  rel_81c_curr_yr_na: '8.1c - Disposal - Applicability',
  rel_81c_curr_yr_qty: '8.1c - Disposal - Current Year Quantity',
  rel_81d_curr_yr_na: '8.1d - Disposal - Applicability',
  rel_81d_curr_yr_qty: '8.1d - Disposal - Current Year Quantity',

  // Section 8.2 - On-site Energy Recovery
  energy_onsite_curr_yr_na: '8.2 - Energy Recovery On-site - Applicability',
  energy_onsite_curr_yr_qty: '8.2 - Energy Recovery On-site - Current Year Quantity',

  // Section 8.3 - Off-site Energy Recovery
  energy_offsite_curr_yr_na: '8.3 - Energy Recovery Off-site - Applicability',
  energy_offsite_curr_yr_qty: '8.3 - Energy Recovery Off-site - Current Year Quantity',

  // Section 8.4 - On-site Recycling
  recyc_onsite_curr_yr_na: '8.4 - Recycling On-site - Applicability',
  recyc_onsite_curr_yr_qty: '8.4 - Recycling On-site - Current Year Quantity',

  // Section 8.5 - Off-site Recycling
  recyc_offsite_curr_yr_na: '8.5 - Recycling Off-site - Applicability',
  recyc_offsite_curr_yr_qty: '8.5 - Recycling Off-site - Current Year Quantity',

  // Section 8.6 - On-site Treatment
  treated_onsite_curr_yr_na: '8.6 - Treatment On-site - Applicability',
  treated_onsite_curr_yr_qty: '8.6 - Treatment On-site - Current Year Quantity',

  // Section 8.7 - Off-site Treatment
  treated_offsite_curr_yr_na: '8.7 - Treatment Off-site - Applicability',
  treated_offsite_curr_yr_qty: '8.7 - Treatment Off-site - Current Year Quantity',

  // Section 8.8 - One-time Release
  one_time_release_qty: '8.8 - One-time Release - Quantity',
  one_time_release_qty_na: '8.8 - One-time Release - Applicability',

  // Section 8.9 - Production Ratio
  prod_ratio_or_activity: '8.9 - Production Ratio or Activity',
  production_ratio: '8.9 - Production Ratio',
  production_ratio_na: '8.9 - Production Ratio - Applicability',
};

function toNumericFlag(input: boolean | null, mode: 'string' | 'number' = 'string'): EpaNumericFlag {
  if (input === null) {
    return null;
  } else {
    if (mode === 'string') {
      if (input === false) {
        return '0';
      } else {
        return '1';
      }
    } else {
      if (input === false) {
        return 0;
      } else {
        return 1;
      }
    }
  }
}

function releaseLineToEz<Prefix extends string>(
  releasesByChemical: ReleasesByChemicalAndFormRLine,
  casOrCategory: CasOrCategory,
  formRLine: FormRLeafLine,
  prefix: Prefix, //'fugitive' | 'stack' | 'uninj_i' | 'ininj_iiv' | 'rcra_c' | 'oth_landf',
): {
  [K in `${Prefix}_basis_est_code`]: string | null;
} & {
  [K in `${Prefix}_release_na`]: string | null;
} & {
  [K in `${Prefix}_tot_rel`]: number | null;
} {
  const releases = releasesByChemical.get(`${casOrCategory}-${formRLine}`);
  if (!releases) {
    return {
      [`${prefix}_basis_est_code`]: null,
      [`${prefix}_release_na`]: '1',
      [`${prefix}_tot_rel`]: 0,
    } as any;
  }

  return {
    [`${prefix}_basis_est_code`]: formRBasisFromTransfers(releases.transfers, {
      chemicalName: '',
      casOrCategory,
      formRLine,
      warnings: [],
    }),
    [`${prefix}_release_na`]: '0',
    [`${prefix}_tot_rel`]: releases.amountReleased,
  } as any;
}

interface WaterReleaseForComparison {
  water_basis_est_code: string | null;
  water_body_name: string | null;
  water_release: number | null;
  water_release_na: string | null;
  water_storm_water_na: string | null;
  storm_water_percent: number | null;
  reach_code: string | null;
}

function releaseLineToWater(OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[]): WaterReleaseForComparison[] {
  const result: WaterReleaseForComparison[] = [];

  for (const release of OnsiteReleaseQuantity) {
    if (release.EnvironmentalMediumCode !== 'WATER') {
      continue;
    }

    // These should always be set for type WATER, but the XML schema doesn't enforce that:
    if (
      release.OnsiteWasteQuantity?.Quantity.choice !== 0 ||
      !release.WaterStream ||
      release.WaterStream?.StormWaterPercent?.choice !== 'ReleaseStormWaterPercent' ||
      typeof release.OnsiteWasteQuantity.QuantityBasis.value !== 'string'
    ) {
      continue;
    }

    result.push({
      water_basis_est_code: release.OnsiteWasteQuantity.QuantityBasis.value,
      water_body_name: release.WaterStream.StreamName,
      water_release: release.OnsiteWasteQuantity.Quantity.value.WasteQuantityMeasure,
      water_release_na: '0',
      water_storm_water_na: '0',
      storm_water_percent: release.WaterStream.StormWaterPercent.value,
      reach_code: release.WaterStream.StreamReachCode,
    });
  }

  if (result.length === 0) {
    result.push({
      water_basis_est_code: null,
      water_body_name: 'NA',
      water_release: 0,
      water_release_na: '1',
      water_storm_water_na: '0', // Apparently even when all of 5.3 is NA, the EPA marks this as applicable.
      storm_water_percent: null,
      reach_code: null,
    });
  }

  while (result.length < 3) {
    result.push({
      water_basis_est_code: null,
      water_body_name: null,
      water_release: null,
      water_release_na: null,
      water_storm_water_na: null,
      storm_water_percent: null,
      reach_code: null,
    });
  }

  return result;
}

interface PotwFieldsForComparison {
  potw_basis_est_code_1: string | null;
  potw_basis_est_code_2: string | null;
  potw_1_total_transfer: number | null;
  potw_2_total_transfer: number | null;
  potw_p30_disch_waster: number | null;
  potw_p31_disch_other_act: number | null;
  potw_p32_released_air: number | null;
  potw_p33_sludge_disposal: number | null;
  potw_p34_sludge_inc_metals: number | null;
  potw_p35_sludge_agric: number | null;
  potw_p36_other_disposal: number | null;
  potw_p37_other_treatment: number | null;
  potw_p38_sludge_incin: number | null;
  potw_p39_exper_treatment: number | null;
  potw_tot_transfer: number | null;
}
function potwSumByCodeForComparison(POTWWasteQuantity: TRIPOTWWasteQuantityDataType[]): PotwFieldsForComparison {
  const result: PotwFieldsForComparison = {
    potw_basis_est_code_1: null,
    potw_basis_est_code_2: null,
    potw_1_total_transfer: 0,
    potw_2_total_transfer: null,
    potw_p30_disch_waster: null,
    potw_p31_disch_other_act: null,
    potw_p32_released_air: null,
    potw_p33_sludge_disposal: null,
    potw_p34_sludge_inc_metals: null,
    potw_p35_sludge_agric: null,
    potw_p36_other_disposal: null,
    potw_p37_other_treatment: null,
    potw_p38_sludge_incin: null,
    potw_p39_exper_treatment: null,
    potw_tot_transfer: 0,
  };

  const sequenceNumberMapping = new Map<number, number>();

  for (const quantity of POTWWasteQuantity) {
    // We don't produce any other option:
    if (
      quantity.WasteQuantity.choice !== 0 ||
      quantity.QuantityBasisEstimation.choice !== 'QuantityBasisEstimationCode' ||
      typeof quantity.POTWTransferSequenceNumber !== 'number'
    ) {
      continue;
    }

    if (!sequenceNumberMapping.has(quantity.POTWTransferSequenceNumber)) {
      sequenceNumberMapping.set(quantity.POTWTransferSequenceNumber, sequenceNumberMapping.size + 1);
    }
    const potwIndex = sequenceNumberMapping.get(quantity.POTWTransferSequenceNumber)!;

    if (potwIndex === 1) {
      result.potw_1_total_transfer =
        (result.potw_1_total_transfer ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;

      if (result.potw_basis_est_code_1 === null) {
        result.potw_basis_est_code_1 = quantity.QuantityBasisEstimation.value;
      }
    } else if (potwIndex === 2) {
      result.potw_2_total_transfer =
        (result.potw_2_total_transfer ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;

      if (result.potw_basis_est_code_2 === null && potwIndex === 2) {
        result.potw_basis_est_code_2 = quantity.QuantityBasisEstimation.value;
      }
    }

    result.potw_tot_transfer = (result.potw_tot_transfer ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;

    if (quantity.POTWTransferTypeCode === 'P30') {
      result.potw_p30_disch_waster =
        (result.potw_p30_disch_waster ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P31') {
      result.potw_p31_disch_other_act =
        (result.potw_p31_disch_other_act ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P32') {
      result.potw_p32_released_air =
        (result.potw_p32_released_air ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P33') {
      result.potw_p33_sludge_disposal =
        (result.potw_p33_sludge_disposal ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P34') {
      result.potw_p34_sludge_inc_metals =
        (result.potw_p34_sludge_inc_metals ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P35') {
      result.potw_p35_sludge_agric =
        (result.potw_p35_sludge_agric ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P36') {
      result.potw_p36_other_disposal =
        (result.potw_p36_other_disposal ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P37') {
      result.potw_p37_other_treatment =
        (result.potw_p37_other_treatment ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P38') {
      result.potw_p38_sludge_incin =
        (result.potw_p38_sludge_incin ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }

    if (quantity.POTWTransferTypeCode === 'P39') {
      result.potw_p39_exper_treatment =
        (result.potw_p39_exper_treatment ?? 0) + quantity.WasteQuantity.value.WasteQuantityMeasure;
    }
  }

  if (sequenceNumberMapping.size > 0) {
    result.potw_p30_disch_waster = result.potw_p30_disch_waster ?? 0;
    result.potw_p31_disch_other_act = result.potw_p31_disch_other_act ?? 0;
    result.potw_p32_released_air = result.potw_p32_released_air ?? 0;
    result.potw_p33_sludge_disposal = result.potw_p33_sludge_disposal ?? 0;
    result.potw_p34_sludge_inc_metals = result.potw_p34_sludge_inc_metals ?? 0;
    result.potw_p35_sludge_agric = result.potw_p35_sludge_agric ?? 0;
    result.potw_p36_other_disposal = result.potw_p36_other_disposal ?? 0;
    result.potw_p37_other_treatment = result.potw_p37_other_treatment ?? 0;
    result.potw_p38_sludge_incin = result.potw_p38_sludge_incin ?? 0;
    result.potw_p39_exper_treatment = result.potw_p39_exper_treatment ?? 0;
  }

  return result;
}

function potwToNamesForComparison(TransferLocation: TRITransferLocationDataType[]): {
  potw_name_1: string | null;
  potw_name_2: string | null;
} {
  const potws = TransferLocation.filter((l) => l.POTWIndicator === true);
  if (potws.length === 0) {
    return {
      potw_name_1: 'NA',
      potw_name_2: null,
    };
  }

  return {
    potw_name_1: potws[0].FacilitySiteName,
    potw_name_2: potws[1]?.FacilitySiteName ?? null,
  };
}

interface OffSiteLocationFieldsForComparison {
  off_site_loc_name_1: string | null;
  off_site_loc_name_2: string | null;
  off_site_loc_name_3: string | null;
  off_site_loc_name_4: string | null;
  off_site_loc_name_5: string | null;
  disp_land_treatment: number; // M73 Land Treatment
  disp_landfill_surf_imp: number; // M64 Other Landfills M65 RCRA Subtitle C Landfills M66 Subtitle C Surface Impoundments M67 Other Surface Impoundments
  disp_other_land_disp: number; // M79 Other Land Disposal
  disp_other_offsite_mgmt: number; // M90 Other Off-Site Management
  disp_solid_stab: number; // M41 Solidification/Stabilization
  disp_storage_only: number; // M10 Storage Only
  disp_trans_waste_broker: number; // M94 Transfer to Waste Broker - Disposal
  disp_ungrnd_inj: number; // M81 Underground Injection to Class I Wells M82 Underground Injection to Class II-V Wells
  disp_wastewater_treat: number; // M62 Wastewater Treatment (Excluding POTW)
  disp_unknown: number; // M99 Management Method Unknown
}

export function offSiteLocationSumByCodeForComparison(
  TransferLocation: TRITransferLocationDataType[],
): OffSiteLocationFieldsForComparison {
  const result: OffSiteLocationFieldsForComparison = {
    off_site_loc_name_1: null,
    off_site_loc_name_2: null,
    off_site_loc_name_3: null,
    off_site_loc_name_4: null,
    off_site_loc_name_5: null,
    disp_land_treatment: 0,
    disp_landfill_surf_imp: 0,
    disp_other_land_disp: 0,
    disp_other_offsite_mgmt: 0,
    disp_solid_stab: 0,
    disp_storage_only: 0,
    disp_trans_waste_broker: 0,
    disp_ungrnd_inj: 0,
    disp_wastewater_treat: 0,
    disp_unknown: 0,
  };

  let index = 0;
  for (const location of TransferLocation) {
    if (location.POTWIndicator === true) {
      continue;
    }

    index++;
    if (index === 1) {
      result.off_site_loc_name_1 = location.FacilitySiteName;
    } else if (index === 2) {
      result.off_site_loc_name_2 = location.FacilitySiteName;
    } else if (index === 3) {
      result.off_site_loc_name_3 = location.FacilitySiteName;
    } else if (index === 4) {
      result.off_site_loc_name_4 = location.FacilitySiteName;
    } else if (index === 5) {
      result.off_site_loc_name_5 = location.FacilitySiteName;
    }

    for (const transfer of location.TransferQuantity) {
      if (transfer.TransferWasteQuantity?.Quantity.choice !== 0 || transfer.WasteManagementTypeCode === null) {
        continue;
      }

      const quantity = transfer.TransferWasteQuantity.Quantity.value.WasteQuantityMeasure;
      if (transfer.WasteManagementTypeCode === 'M73') {
        result.disp_land_treatment += quantity;
      } else if (
        transfer.WasteManagementTypeCode === 'M64' ||
        transfer.WasteManagementTypeCode === 'M65' ||
        transfer.WasteManagementTypeCode === 'M66' ||
        transfer.WasteManagementTypeCode === 'M67'
      ) {
        result.disp_landfill_surf_imp += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M79') {
        result.disp_other_land_disp += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M90') {
        result.disp_other_offsite_mgmt += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M41') {
        result.disp_solid_stab += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M10') {
        result.disp_storage_only += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M94') {
        result.disp_trans_waste_broker += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M81' || transfer.WasteManagementTypeCode === 'M82') {
        result.disp_ungrnd_inj += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M62') {
        result.disp_wastewater_treat += quantity;
      } else if (transfer.WasteManagementTypeCode === 'M99') {
        result.disp_unknown += quantity;
      }
    }
  }

  if (index === 0) {
    result.off_site_loc_name_1 = 'NA';
  }

  return result;
}

function section8CurrentYear(years: TRITotalYearlyQuantityDataType[]): TRITotalYearlyQuantityDataType['Quantity'] {
  return years.find((y) => y.YearOffsetMeasure === 0)!.Quantity;
}

interface Section8ForComparison {
  // Section 8.1
  rel_81a_curr_yr_na: string | null;
  rel_81a_curr_yr_qty: number | null;
  rel_81b_curr_yr_na: string | null;
  rel_81b_curr_yr_qty: number | null;
  rel_81c_curr_yr_na: string | null;
  rel_81c_curr_yr_qty: number | null;
  rel_81d_curr_yr_na: string | null;
  rel_81d_curr_yr_qty: number | null;

  // Section 8.2
  energy_onsite_curr_yr_na: string | null;
  energy_onsite_curr_yr_qty: number | null;

  // Section 8.3
  energy_offsite_curr_yr_na: string | null;
  energy_offsite_curr_yr_qty: number | null;

  // Section 8.4
  recyc_onsite_curr_yr_na: string | null;
  recyc_onsite_curr_yr_qty: number | null;

  // Section 8.5
  recyc_offsite_curr_yr_na: string | null;
  recyc_offsite_curr_yr_qty: number | null;

  // Section 8.6
  treated_onsite_curr_yr_na: string | null;
  treated_onsite_curr_yr_qty: number | null;

  // Section 8.7
  treated_offsite_curr_yr_na: string | null;
  treated_offsite_curr_yr_qty: number | null;

  // Section 8.8
  one_time_release_qty: number | null;
  one_time_release_qty_na: string | null;

  // Section 8.9
  prod_ratio_or_activity: string | null;
  production_ratio: number | null;
  production_ratio_na: string | null;
}

function section8ConvertForComparison(
  SourceReductionQuantity: TRISourceReductionQuantityDataType,
): Section8ForComparison {
  const result: Section8ForComparison = {
    // Section 8.1
    rel_81a_curr_yr_na: null,
    rel_81a_curr_yr_qty: null,
    rel_81b_curr_yr_na: null,
    rel_81b_curr_yr_qty: null,
    rel_81c_curr_yr_na: null,
    rel_81c_curr_yr_qty: null,
    rel_81d_curr_yr_na: null,
    rel_81d_curr_yr_qty: null,

    // Section 8.2
    energy_onsite_curr_yr_na: null,
    energy_onsite_curr_yr_qty: null,

    // Section 8.3
    energy_offsite_curr_yr_na: null,
    energy_offsite_curr_yr_qty: null,

    // Section 8.4
    recyc_onsite_curr_yr_na: null,
    recyc_onsite_curr_yr_qty: null,

    // Section 8.5
    recyc_offsite_curr_yr_na: null,
    recyc_offsite_curr_yr_qty: null,

    // Section 8.6
    treated_onsite_curr_yr_na: null,
    treated_onsite_curr_yr_qty: null,

    // Section 8.7
    treated_offsite_curr_yr_na: null,
    treated_offsite_curr_yr_qty: null,

    // Section 8.8
    one_time_release_qty: null,
    one_time_release_qty_na: null,

    // Section 8.9
    prod_ratio_or_activity: null,
    production_ratio: null,
    production_ratio_na: null,
  };

  const OnsiteUICDisposalQuantity = section8CurrentYear(SourceReductionQuantity.OnsiteUICDisposalQuantity);
  if (OnsiteUICDisposalQuantity.choice === 'NA') {
    result.rel_81a_curr_yr_na = '1';
  } else {
    result.rel_81a_curr_yr_na = '0';
    result.rel_81a_curr_yr_qty = OnsiteUICDisposalQuantity.value.TotalQuantity;
  }

  const OnsiteOtherDisposalQuantity = section8CurrentYear(SourceReductionQuantity.OnsiteOtherDisposalQuantity);
  if (OnsiteOtherDisposalQuantity.choice === 'NA') {
    result.rel_81b_curr_yr_na = '1';
  } else {
    result.rel_81b_curr_yr_na = '0';
    result.rel_81b_curr_yr_qty = OnsiteOtherDisposalQuantity.value.TotalQuantity;
  }

  const OffsiteUICDisposalQuantity = section8CurrentYear(SourceReductionQuantity.OffsiteUICDisposalQuantity);
  if (OffsiteUICDisposalQuantity.choice === 'NA') {
    result.rel_81c_curr_yr_na = '1';
  } else {
    result.rel_81c_curr_yr_na = '0';
    result.rel_81c_curr_yr_qty = OffsiteUICDisposalQuantity.value.TotalQuantity;
  }

  const OffsiteOtherDisposalQuantity = section8CurrentYear(SourceReductionQuantity.OffsiteOtherDisposalQuantity);
  if (OffsiteOtherDisposalQuantity.choice === 'NA') {
    result.rel_81d_curr_yr_na = '1';
  } else {
    result.rel_81d_curr_yr_na = '0';
    result.rel_81d_curr_yr_qty = OffsiteOtherDisposalQuantity.value.TotalQuantity;
  }

  const OnsiteEnergyRecoveryQuantity = section8CurrentYear(SourceReductionQuantity.OnsiteEnergyRecoveryQuantity);
  if (OnsiteEnergyRecoveryQuantity.choice === 'NA') {
    result.energy_onsite_curr_yr_na = '1';
  } else {
    result.energy_onsite_curr_yr_na = '0';
    result.energy_onsite_curr_yr_qty = OnsiteEnergyRecoveryQuantity.value.TotalQuantity;
  }

  const OffsiteEnergyRecoveryQuantity = section8CurrentYear(SourceReductionQuantity.OffsiteEnergyRecoveryQuantity);
  if (OffsiteEnergyRecoveryQuantity.choice === 'NA') {
    result.energy_offsite_curr_yr_na = '1';
  } else {
    result.energy_offsite_curr_yr_na = '0';
    result.energy_offsite_curr_yr_qty = OffsiteEnergyRecoveryQuantity.value.TotalQuantity;
  }

  const OnsiteRecycledQuantity = section8CurrentYear(SourceReductionQuantity.OnsiteRecycledQuantity);
  if (OnsiteRecycledQuantity.choice === 'NA') {
    result.recyc_onsite_curr_yr_na = '1';
  } else {
    result.recyc_onsite_curr_yr_na = '0';
    result.recyc_onsite_curr_yr_qty = OnsiteRecycledQuantity.value.TotalQuantity;
  }

  const OffsiteRecycledQuantity = section8CurrentYear(SourceReductionQuantity.OffsiteRecycledQuantity);
  if (OffsiteRecycledQuantity.choice === 'NA') {
    result.recyc_offsite_curr_yr_na = '1';
  } else {
    result.recyc_offsite_curr_yr_na = '0';
    result.recyc_offsite_curr_yr_qty = OffsiteRecycledQuantity.value.TotalQuantity;
  }

  const OnsiteTreatedQuantity = section8CurrentYear(SourceReductionQuantity.OnsiteTreatedQuantity);
  if (OnsiteTreatedQuantity.choice === 'NA') {
    result.treated_onsite_curr_yr_na = '1';
  } else {
    result.treated_onsite_curr_yr_na = '0';
    result.treated_onsite_curr_yr_qty = OnsiteTreatedQuantity.value.TotalQuantity;
  }

  const OffsiteTreatedQuantity = section8CurrentYear(SourceReductionQuantity.OffsiteTreatedQuantity);
  if (OffsiteTreatedQuantity.choice === 'NA') {
    result.treated_offsite_curr_yr_na = '1';
  } else {
    result.treated_offsite_curr_yr_na = '0';
    result.treated_offsite_curr_yr_qty = OffsiteTreatedQuantity.value.TotalQuantity;
  }

  if (SourceReductionQuantity.ReleaseQuantity.choice === 'NA') {
    result.one_time_release_qty_na = '1';
  } else {
    result.one_time_release_qty_na = '0';
    result.one_time_release_qty = SourceReductionQuantity.ReleaseQuantity.value.OneTimeReleaseQuantity;
  }

  result.prod_ratio_or_activity = SourceReductionQuantity.ProductionRatioType;
  if (SourceReductionQuantity.ProductionRatio.choice === 'NA') {
    result.production_ratio_na = '1';
  } else {
    result.production_ratio_na = '0';
    result.production_ratio = SourceReductionQuantity.ProductionRatio.value;
  }

  return result;
}

export function formRXmlStructureToEz({
  year,
  casOrCategory,
  activities,
  maximumChemicalAmountCode,
  releasesByChemical,
  OnsiteReleaseQuantity,
  POTWWasteQuantity,
  TransferLocation,
  OnsiteRecoveryProcess,
  OnsiteRecyclingProcess,
  SourceReductionQuantity,
}: {
  year: number;
  casOrCategory: CasOrCategory;
  activities: TRIChemicalActivitiesAndUsesDataType;
  maximumChemicalAmountCode: TRIMaximumChemicalAmountCodeType;
  releasesByChemical: ReleasesByChemicalAndFormRLine;
  OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[];
  POTWWasteQuantity: TRIPOTWWasteQuantityDataType[];
  TransferLocation: TRITransferLocationDataType[];
  OnsiteRecoveryProcess: TRIOnsiteRecoveryProcessDataType;
  OnsiteRecyclingProcess: TRIOnsiteRecyclingProcessDataType;
  SourceReductionQuantity: TRISourceReductionQuantityDataType;
}): EpaApiTriFormREZForComparison {
  const water = releaseLineToWater(OnsiteReleaseQuantity);

  return {
    // Chemical and document info
    reporting_year: String(year),
    cas_registry_number: casOrCategory,

    // Section 3 - Manufacture
    manufacture: toNumericFlag(activities.ChemicalProducedIndicator || activities.ChemicalImportedIndicator),
    produce: toNumericFlag(activities.ChemicalProducedIndicator),
    imported: toNumericFlag(activities.ChemicalImportedIndicator),
    used_processed: toNumericFlag(activities.ChemicalUsedProcessedIndicator),
    sale_distribution: toNumericFlag(activities.ChemicalSalesDistributionIndicator),
    byproduct: toNumericFlag(activities.ChemicalByproductIndicator),
    manufacture_impurity: toNumericFlag(activities.ChemicalManufactureImpurityIndicator),

    // Section 3 - Process
    process: toNumericFlag(
      activities.ChemicalReactantIndicator ||
        activities.ChemicalFormulationComponentIndicator ||
        activities.ChemicalArticleComponentIndicator ||
        activities.ChemicalRepackagingIndicator ||
        activities.ChemicalProcessImpurityIndicator ||
        activities.ChemicalProcessRecyclingIndicator,
    ),
    reactant: toNumericFlag(activities.ChemicalReactantIndicator),
    ...(activities.ChemicalReactantIndicator === true
      ? {
          reactant_feedstocks: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P101')),
          reactant_initiators: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P104')),
          reactant_intermediates: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P103')),
          reactant_other: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P199')),
          reactant_raw_materials: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P102')),
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // reactant_catalyst: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P105')),
          // reactant_binder: toNumericFlag(activities.ChemicalReactantSubcategory.includes('P106')),
        }
      : {
          reactant_feedstocks: null,
          reactant_initiators: null,
          reactant_intermediates: null,
          reactant_other: null,
          reactant_raw_materials: null,
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // reactant_catalyst: null,
          // reactant_binder: null,
        }),

    formulation_component: toNumericFlag(activities.ChemicalFormulationComponentIndicator),
    ...(activities.ChemicalFormulationComponentIndicator === true
      ? {
          formul_comp_additives: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P201')),
          formul_comp_dyes: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P202')),
          formul_comp_emulsifiers: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P207')),
          formul_comp_flame_retardants: toNumericFlag(
            activities.ChemicalFormulationComponentSubcategory.includes('P210'),
          ),
          formul_comp_inhibitors: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P206')),
          formul_comp_initiators: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P204')),
          formul_comp_lubricants: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P209')),
          formul_comp_other: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P299')),
          formul_comp_reac_diluents: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P203')),
          formul_comp_rheological_mods: toNumericFlag(
            activities.ChemicalFormulationComponentSubcategory.includes('P211'),
          ),
          formul_comp_solvents: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P205')),
          formul_comp_surfactants: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P208')),
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // formul_comp_binder: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P212')),
          // formul_comp_fuel_agents: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P213')),
          // formul_comp_deodorizer: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P214')),
          // formul_comp_fragrance: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P215')),
          // formul_comp_plasticizer: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P216')),
          // formul_comp_semiconductor: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P217')),
          // formul_comp_energy_releasers: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P218'),
          // ),
          // formul_comp_foamant: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P219')),
          // formul_comp_propellants: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P220')),
          // formul_comp_chem_react_reg: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P221'),
          // ),
          // formul_comp_corrosion_inhibitor: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P222'),
          // ),
          // formul_comp_soil_amendments: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P223'),
          // ),
          // formul_comp_sealant: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P224')),
          // formul_comp_explosion_inhibitor: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P225'),
          // ),
          // formul_comp_fire_extinguishing: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P226'),
          // ),
          // formul_comp_flavoring_nutrient: toNumericFlag(
          //   activities.ChemicalFormulationComponentSubcategory.includes('P227'),
          // ),
          // formul_comp_embalming: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P228')),
          // formul_comp_preservative: toNumericFlag(activities.ChemicalFormulationComponentSubcategory.includes('P229')),
        }
      : {
          formul_comp_additives: null,
          formul_comp_dyes: null,
          formul_comp_emulsifiers: null,
          formul_comp_flame_retardants: null,
          formul_comp_inhibitors: null,
          formul_comp_initiators: null,
          formul_comp_lubricants: null,
          formul_comp_other: null,
          formul_comp_reac_diluents: null,
          formul_comp_rheological_mods: null,
          formul_comp_solvents: null,
          formul_comp_surfactants: null,
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // formul_comp_binder: null,
          // formul_comp_fuel_agents: null,
          // formul_comp_deodorizer: null,
          // formul_comp_fragrance: null,
          // formul_comp_plasticizer: null,
          // formul_comp_semiconductor: null,
          // formul_comp_energy_releasers: null,
          // formul_comp_foamant: null,
          // formul_comp_propellants: null,
          // formul_comp_chem_react_reg: null,
          // formul_comp_corrosion_inhibitor: null,
          // formul_comp_soil_amendments: null,
          // formul_comp_sealant: null,
          // formul_comp_explosion_inhibitor: null,
          // formul_comp_fire_extinguishing: null,
          // formul_comp_flavoring_nutrient: null,
          // formul_comp_embalming: null,
          // formul_comp_preservative: null,
        }),

    article_component: toNumericFlag(activities.ChemicalArticleComponentIndicator),
    repackaging: toNumericFlag(activities.ChemicalRepackagingIndicator),
    process_impurity: toNumericFlag(activities.ChemicalProcessImpurityIndicator, 'number'),
    processed_recycling: toNumericFlag(activities.ChemicalProcessRecyclingIndicator),

    // Section 3 - Otherwise Use
    otherwise_use: toNumericFlag(
      activities.ChemicalAncillaryUsageIndicator ||
        activities.ChemicalProcessingAidIndicator ||
        activities.ChemicalManufactureAidIndicator,
    ),
    ancillary: toNumericFlag(activities.ChemicalAncillaryUsageIndicator),
    ...(activities.ChemicalAncillaryUsageIndicator === true
      ? {
          ancl_other_use_cleaner: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z301')),
          ancl_other_use_construct_matls: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z308')),
          ancl_other_use_degreaser: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z302')),
          ancl_other_use_flame_retardant: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z305')),
          ancl_other_use_fuel: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z304')),
          ancl_other_use_lubricant: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z303')),
          ancl_other_use_other: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z399')),
          ancl_other_use_waste_treat: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z306')),
          ancl_other_use_water_treat: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z307')),
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // ancl_other_use_deodorizer: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z309')),
          // ancl_other_use_fragrance: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z310')),
          // ancl_other_use_abrasives: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z311')),
          // ancl_other_use_binder: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z312')),
          // ancl_other_use_sealant: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z313')),
          // ancl_other_use_energy_releasers: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z314')),
          // ancl_other_use_etching: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z315')),
          // ancl_other_use_fire_extinguishing: toNumericFlag(
          //   activities.ChemicalAncillaryUsageSubcategory.includes('Z316'),
          // ),
          // ancl_other_use_embalming: toNumericFlag(activities.ChemicalAncillaryUsageSubcategory.includes('Z317')),
        }
      : {
          ancl_other_use_cleaner: null,
          ancl_other_use_construct_matls: null,
          ancl_other_use_degreaser: null,
          ancl_other_use_flame_retardant: null,
          ancl_other_use_fuel: null,
          ancl_other_use_lubricant: null,
          ancl_other_use_other: null,
          ancl_other_use_waste_treat: null,
          ancl_other_use_water_treat: null,
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // ancl_other_use_deodorizer: null,
          // ancl_other_use_fragrance: null,
          // ancl_other_use_abrasives: null,
          // ancl_other_use_binder: null,
          // ancl_other_use_sealant: null,
          // ancl_other_use_energy_releasers: null,
          // ancl_other_use_etching: null,
          // ancl_other_use_fire_extinguishing: null,
          // ancl_other_use_embalming: null,
        }),

    chem_processing_aid: toNumericFlag(activities.ChemicalProcessingAidIndicator),
    ...(activities.ChemicalProcessingAidIndicator === true
      ? {
          chem_process_aid_catalysts: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z102')),
          chem_process_aid_inhibitors: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z103')),
          chem_process_aid_initiators: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z104')),
          chem_process_aid_other: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z199')),
          chem_process_aid_process_solvs: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z101')),
          chem_process_aid_react_termin: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z105')),
          chem_process_aid_solution_buf: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z106')),
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // chem_process_aid_corrosion_inhibitor: toNumericFlag(
          //   activities.ChemicalProcessingAidSubcategory.includes('Z107'),
          // ),
          // chem_process_aid_absorbent: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z108')),
          // chem_process_aid_adsorbent: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z109')),
          // chem_process_aid_semiconductor: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z110')),
          // chem_process_aid_explosion_inhibitor: toNumericFlag(
          //   activities.ChemicalProcessingAidSubcategory.includes('Z111'),
          // ),
          // chem_process_aid_tracer: toNumericFlag(activities.ChemicalProcessingAidSubcategory.includes('Z112')),
        }
      : {
          chem_process_aid_catalysts: null,
          chem_process_aid_inhibitors: null,
          chem_process_aid_initiators: null,
          chem_process_aid_other: null,
          chem_process_aid_process_solvs: null,
          chem_process_aid_react_termin: null,
          chem_process_aid_solution_buf: null,
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // chem_process_aid_corrosion_inhibitor: null,
          // chem_process_aid_absorbent: null,
          // chem_process_aid_adsorbent: null,
          // chem_process_aid_semiconductor: null,
          // chem_process_aid_explosion_inhibitor: null,
          // chem_process_aid_tracer: null,
        }),

    manufacture_aid: toNumericFlag(activities.ChemicalManufactureAidIndicator),
    ...(activities.ChemicalManufactureAidIndicator === true
      ? {
          manuf_aid_coolants: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z203')),
          manuf_aid_hydraulic_fluids: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z205')),
          manuf_aid_metalworking_fluids: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z202')),
          manuf_aid_other: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z299')),
          manuf_aid_process_lubricants: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z201')),
          manuf_aid_refrigerants: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z204')),
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // manuf_aid_corrosion_inhibitor: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z206')),
          // manuf_aid_heat_transfer: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z207')),
          // manuf_aid_refrigerants_z208: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z208')),
          // manuf_aid_plating: toNumericFlag(activities.ChemicalManufactureAidSubcategory.includes('Z209')),
        }
      : {
          manuf_aid_coolants: null,
          manuf_aid_hydraulic_fluids: null,
          manuf_aid_metalworking_fluids: null,
          manuf_aid_other: null,
          manuf_aid_process_lubricants: null,
          manuf_aid_refrigerants: null,
          // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
          // manuf_aid_corrosion_inhibitor: null,
          // manuf_aid_heat_transfer: null,
          // manuf_aid_refrigerants_z208: null,
          // manuf_aid_plating: null,
        }),

    // Section 4
    max_amount_of_chem: maximumChemicalAmountCode,

    // Section 5 (except 5.3)
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.1', 'fugitive'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.2', 'stack'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.4.1', 'uninj_i'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.4.2', 'uninj_iiv'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.1A', 'rcra_c'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.1B', 'oth_landf'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.2', 'land_trea'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.3A', 'sub_surf_imp'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.3B', 'oth_surf_imp'),
    ...releaseLineToEz(releasesByChemical, casOrCategory, '5.5.4', 'oth_disp'),

    // Section 5.3
    water_basis_est_code_1: water[0].water_basis_est_code,
    water_basis_est_code_2: water[1].water_basis_est_code,
    water_basis_est_code_3: water[2].water_basis_est_code,
    water_body_name_1: water[0].water_body_name,
    water_body_name_2: water[1].water_body_name,
    water_body_name_3: water[2].water_body_name,
    water_release_1: water[0].water_release,
    water_release_2: water[1].water_release,
    water_release_3: water[2].water_release,
    water_release_na_1: water[0].water_release_na,
    water_release_na_2: water[1].water_release_na,
    water_release_na_3: water[2].water_release_na,
    water_storm_water_na_1: water[0].water_storm_water_na,
    water_storm_water_na_2: water[1].water_storm_water_na,
    water_storm_water_na_3: water[2].water_storm_water_na,
    storm_water_percent_1: water[0].storm_water_percent,
    storm_water_percent_2: water[1].storm_water_percent,
    storm_water_percent_3: water[2].storm_water_percent,
    reach_code_1: water[0].reach_code === '' ? null : water[0].reach_code,
    reach_code_2: water[1].reach_code === '' ? null : water[1].reach_code,
    reach_code_3: water[2].reach_code === '' ? null : water[2].reach_code,

    // Section 6.1
    ...potwToNamesForComparison(TransferLocation),
    ...potwSumByCodeForComparison(POTWWasteQuantity),

    // Section 6.2
    ...offSiteLocationSumByCodeForComparison(TransferLocation),

    // Section 7A requires a separate data fetch, and so is handled elsewhere.

    // Section 7B
    onsite_energy_proc_codes:
      OnsiteRecoveryProcess.choice === 'EnergyRecoveryMethodCode' ? OnsiteRecoveryProcess.value.join('') : 'NA',

    // Section 7C
    onsite_recycle_proc_codes:
      OnsiteRecyclingProcess.choice === 'OnsiteRecyclingMethodCode' ? OnsiteRecyclingProcess.value.join('') : 'NA',

    ...section8ConvertForComparison(SourceReductionQuantity),
  };
}
